'use client';

import { MXP_BASE_URL } from '@lib/config-client';
import { usePageViewContext } from '@tuic/tracking';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { moduleToRoutePath } from './module-to-route-v2';
import { useMxpIframe } from './use-mxp-iframe';

import type {
  ModuleReadyV2Payload,
  MxpModuleIdV2,
  MxpV2AuxFavoriteAddedHandler,
  MxpV2AuxHeightChangeHandler,
  MxpV2AuxJwtLoginErrorHandler,
  MxpV2AuxNavigateHandler,
  MxpV2AuxShareHandler,
  MxpV2AuxTitleChangeHandler,
  MxpV2State,
} from './mxp365-v2.types';
import type { IframeHTMLAttributes } from 'react';

type Props = Omit<IframeHTMLAttributes<HTMLIFrameElement>, 'src'> & {
  token: string;
  queryParams?: Record<string, string>;
  initialModule?: MxpModuleIdV2;
  initialParameter?: string | null;
  timeoutMs?: number;
  onReady?: (payload: ModuleReadyV2Payload) => void;
  onEvent?: (payload: ModuleReadyV2Payload) => void;
  onMxpShare?: MxpV2AuxShareHandler;
  onMxpFavoriteAdded?: MxpV2AuxFavoriteAddedHandler;
  onMxpHeightChange?: MxpV2AuxHeightChangeHandler;
  onMxpNavigate?: MxpV2AuxNavigateHandler;
  onMxpTitleChange?: MxpV2AuxTitleChangeHandler;
  onMxpJwtLoginError?: MxpV2AuxJwtLoginErrorHandler;
  onLoad?: () => void;
};

export type MxpIframeV2Handle = {
  iframe: HTMLIFrameElement | null;
  navigate: (
    module: MxpModuleIdV2,
    parameter?: string | null,
  ) => Promise<ModuleReadyV2Payload>;
  reload: () => void;
  getState: () => MxpV2State;
  postMessage: (message: { action: string; payload?: unknown }) => void;
  getIframeUrl: () => URL;
};

export const Mxp365IframeV2 = forwardRef<MxpIframeV2Handle, Props>(
  function Mxp365IframeV2(props, forwardedRef) {
    const {
      token,
      queryParams,
      initialModule,
      initialParameter,
      timeoutMs,
      onReady,
      onEvent,
      onMxpShare,
      onMxpFavoriteAdded,
      onMxpHeightChange,
      onMxpNavigate,
      onMxpTitleChange,
      onMxpJwtLoginError,
      onLoad,
      ...iframeProps
    } = props;

    const pageViewData = usePageViewContext();

    const targetOrigin = useMemo(() => new URL(MXP_BASE_URL).origin, []);

    // Compute the full URL from current props. Used as the iframe src at
    // mount time and whenever reload() is called. Changes to props after
    // mount do NOT change the iframe src — all post-mount navigation goes
    // through navigateToV2 postMessages so there is no src/postMessage race.
    const iframeSource = useMemo(() => {
      const routePath = initialModule
        ? moduleToRoutePath(initialModule, initialParameter)
        : '';
      const urlObj = new URL(`${MXP_BASE_URL}/mxp365tiles/${routePath}`);

      if (token) {
        urlObj.searchParams.set('token', token);
      }

      if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
          urlObj.searchParams.set(key, value);
        });
      }

      if (pageViewData) {
        Object.entries(pageViewData).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          const camelKey = key.replace(/_(.)/g, (_match, c: string) =>
            c.toUpperCase(),
          );
          if (!urlObj.searchParams.has(camelKey)) {
            urlObj.searchParams.set(camelKey, String(value));
          }
        });
      }

      return urlObj.toString();
    }, [initialModule, initialParameter, token, queryParams, pageViewData]);

    // Frozen at mount time. Re-renders that change iframeSource (e.g. because
    // the dialog updated currentModule) must NOT change the iframe src attribute —
    // that would trigger a direct-load which races with the pending navigateToV2
    // request and causes a CLIENT_TIMEOUT.
    // The ref resets on remount (key change), so an intentional full reload
    // still picks up the new module URL.
    const frozenSourceRef = useRef(iframeSource);
    const frozenSource = frozenSourceRef.current;

    const { iframeRef, state, navigate, reload, postMessage } = useMxpIframe({
      src: frozenSource,
      targetOrigin,
      timeoutMs,
      onEvent,
      onReady,
      onShare: onMxpShare,
      onFavoriteAdded: onMxpFavoriteAdded,
      onHeightChange: onMxpHeightChange,
      onNavigate: onMxpNavigate,
      onTitleChange: onMxpTitleChange,
      onJwtLoginError: onMxpJwtLoginError,
    });

    const stateRef = useRef(state);
    useEffect(() => {
      stateRef.current = state;
    }, [state]);

    useImperativeHandle<MxpIframeV2Handle, MxpIframeV2Handle>(
      forwardedRef,
      () => ({
        get iframe() {
          return iframeRef.current;
        },
        navigate,
        reload,
        getState: () => stateRef.current,
        postMessage,
        getIframeUrl: () => new URL(frozenSource),
      }),
      [iframeRef, navigate, reload, postMessage, frozenSource],
    );

    return (
      <iframe
        ref={iframeRef}
        src={frozenSource}
        title={iframeProps.title ?? 'MXP365 Module'}
        onLoad={onLoad}
        tabIndex={0}
        {...iframeProps}
        allow={iframeProps.allow ?? 'camera'}
      />
    );
  },
);
